import React, { useState, useRef, useEffect, ChangeEvent, forwardRef, useImperativeHandle } from 'react';
import * as PortOne from '@portone/browser-sdk/v2';
import type { IdentityVerificationResponse, IdentityVerificationRequest } from '@portone/browser-sdk/v2';
import axios from 'axios';
import { trackEvent } from '../../lib/analytics';

type PortoneTestProps = {
  authorEmail: string;
  authorName: string;
  setAuthorName: (name: string) => void;
  setIsRealNameVerified: (verified: boolean) => void;
  setRealNameAuthError?: (error: string) => void;
  isInitiallyVerified?: boolean;
  isVerificationStatusLoading?: boolean;
};

const PortoneTest = forwardRef<HTMLInputElement, PortoneTestProps>((props, ref) => {

  const {
    authorEmail,
    authorName,
    setAuthorName,
    setIsRealNameVerified,
    setRealNameAuthError,
    isInitiallyVerified = false,
    isVerificationStatusLoading = false,
  } = props;





  if (!authorEmail) {

    const email = localStorage.getItem("email");

    //console.log("eamil>>:::" , email)


    // window.location.reload(); // 페이지 강제 새로고침
  }
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [verificationStatus, setVerificationStatus] =
    useState<'idle' | 'pending' | 'success' | 'failure'>('idle');
  const [message, setMessage] = useState<string>('');
  const internalNameRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => internalNameRef.current as HTMLInputElement);

  useEffect(() => {
    if (isInitiallyVerified) {
      setVerificationStatus('success');
      setMessage('본인인증이 완료되었습니다.');
      setErrors({});
      if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(true);
      if (typeof setRealNameAuthError === 'function') setRealNameAuthError('');
    }
  }, [isInitiallyVerified, setIsRealNameVerified, setRealNameAuthError]);

  const handleAuthorNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAuthorName(e.target.value);
    setErrors({});
    setMessage('');
    if (typeof setRealNameAuthError === 'function') {
      setRealNameAuthError('');
    }
    // setIsRealNameVerified가 함수인지 확인 후 호출
    if (typeof setIsRealNameVerified === 'function') {
      setIsRealNameVerified(false); // 이름 변경 시 재인증 필요
    }
    if (verificationStatus !== 'idle') setVerificationStatus('idle');
  };

  const realNameAuthentication = async (name: string) => {
    if (!name.trim()) {
      setErrors({ name: '이름을 입력해주세요.' });
      if (typeof setRealNameAuthError === 'function') {
        setRealNameAuthError('이름을 입력해주세요.');
      }
      return;
    }
    if (typeof setRealNameAuthError === 'function') {
      setRealNameAuthError('');
    }



    if (!PortOne.requestIdentityVerification) {
      setMessage('본인인증 서비스가 준비되지 않았습니다.');
      return;
    }

    if (!authorEmail) {
      alert("이메일을 먼저 입력해주세요.");
      return;
    }

    // GA4 이벤트 전송: 실명인증 버튼 클릭
    trackEvent('click_identity_verification', {
      page_path: window.location.pathname,
    });

    setVerificationStatus('pending');
    setMessage('본인인증을 시작합니다...');

    try {
      const identityVerificationId = `identity-verification-${crypto.randomUUID()}`;

      const options: IdentityVerificationRequest = {
        storeId: 'store-6a018743-ac18-4fa2-9eaa-82e03732e4f1',
        identityVerificationId,
        channelKey: 'channel-key-ef0e62f0-a885-4eab-9d3f-cd36da31dafc',

        customer: { fullName: name },
        redirectUrl: `${window.location.origin}/addAuthor`,
      };

      // 로컬 스토리지에 저장 (redirect 후 복원용)
      localStorage.setItem('identityVerificationId', identityVerificationId);
      localStorage.setItem('authorName', name);



      const response: IdentityVerificationResponse | undefined =
        await PortOne.requestIdentityVerification(options);

      if (!response) {
        // 모바일 환경의 redirect 예상
        setMessage('인증 완료 후 돌아오면 자동으로 처리됩니다.');
        return;
      }

      // 데스크톱 환경: 응답으로 처리
      if (response.code) {
        setVerificationStatus('failure');
        setMessage(response.message || '본인인증 중 오류가 발생했습니다.');
        if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(false);
        return;
      }

      const txId = response.identityVerificationTxId;

      const verificationPayload = {
        identityVerificationId: identityVerificationId,
        txId,
        impUid: identityVerificationId,
        authorEmail
      };

      //console.log("Sending verification payload:", verificationPayload);

      const backendResponse = await axios.post('/api/v1/portone/verify-identity', verificationPayload);

      const backendData = backendResponse.data;


      if (backendData?.name === name) {
        setVerificationStatus('success');
        setMessage('본인인증에 성공했습니다.');
        if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(true);
        if (typeof setRealNameAuthError === 'function') setRealNameAuthError('');

        // GA4 이벤트 전송: 실명인증 완료
        trackEvent('complete_identity_verification', {
          page_path: window.location.pathname,
        });
      } else {
        setVerificationStatus('failure');
        let errMsg = '';
        if (backendData?.name && backendData.name !== name) {
          errMsg = '실명인증에 사용한 이름을 입력해 주세요.';
        } else {
          errMsg = backendData?.message || '본인인증 검증에 실패했습니다.';
        }
        if (typeof errMsg === 'string' && (errMsg.includes('Already verified') || errMsg.includes('이미 가입된 이름입니다') || errMsg.includes('이미 등록된') || errMsg.includes('이미 가입된'))) {
          const baseMsg = errMsg.trim().endsWith('.') ? errMsg.trim() : `${errMsg.trim()}.`;
          errMsg = `${baseMsg}\n다른 계정으로 이미 작가 등록된 내역이 있는지 확인해 주세요. 만약 그렇지 않다면 북밋 관리자에게 메일을 보내주시기 바랍니다.`;
        }
        setMessage(errMsg);
        if (typeof setRealNameAuthError === 'function') {
          setRealNameAuthError(errMsg);
        }
        if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(false);
      }


    } catch (error: any) {
      setVerificationStatus('failure');
      const errData = error.response?.data;
      const errMsg = typeof errData === 'string' ? errData : errData?.message || error.message;

      let userErrMsg = '';
      if (typeof errMsg === 'string' && (errMsg.includes('Already verified') || errMsg.includes('이미 가입된 이름입니다') || errMsg.includes('이미 등록된') || errMsg.includes('이미 가입된'))) {
        const baseMsg = errMsg.trim().endsWith('.') ? errMsg.trim() : `${errMsg.trim()}.`;
        userErrMsg = `${baseMsg}\n다른 계정으로 이미 작가 등록 내역이 있는지 확인해 주세요. 만약 그렇지 않다면 북밋 관리자에게 메일을 보내주시기 바랍니다.`;
      } else {
        userErrMsg = `본인인증 처리 중 오류가 발생했습니다: ${errMsg}`;
      }
      setMessage(userErrMsg);
      if (typeof setRealNameAuthError === 'function') {
        setRealNameAuthError(userErrMsg);
      }

      if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(false);
    } finally {
      localStorage.removeItem('identityVerificationId');
      localStorage.removeItem('authorName');
    }
  };

  // 리다이렉트 후 인증 복원 처리
  useEffect(() => {
    const url = new URL(window.location.href);
    const isRedirect = url.pathname === '/addAuthor';
    const identityVerificationId = localStorage.getItem('identityVerificationId');
    const name = localStorage.getItem('authorName');

    if (isRedirect && identityVerificationId && name) {
      const verify = async () => {
        setVerificationStatus('pending');
        setMessage('본인인증 결과를 확인 중입니다...');

        const email = authorEmail || localStorage.getItem("email") || "";

        try {
          const verificationPayload = {
            identityVerificationId,
            impUid: identityVerificationId,
            authorEmail: email
          }

          const backendResponse = await axios.post('/api/v1/portone/verify-identity', verificationPayload);

          const backendData = backendResponse.data;
          console.log("=== 모바일 리다이렉트 본인인증 성공 후 백엔드 응답 데이터 ===", backendData);
          const verifiedName = backendData?.name;

          if (backendResponse.status === 200 && verifiedName === name) {
            setAuthorName(name); // 리다이렉트 시 상태 복원
            setVerificationStatus('success');
            if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(true);
            if (typeof setRealNameAuthError === 'function') setRealNameAuthError('');
            setMessage('본인인증에 성공했습니다.');

            // GA4 이벤트 전송: 실명인증 완료
            trackEvent('complete_identity_verification', {
              page_path: window.location.pathname,
            });
          } else {
            setVerificationStatus('failure');
            let errMsg = '';
            if (verifiedName && verifiedName !== name) {
              errMsg = '실명인증에 사용한 이름을 입력해 주세요.';
            } else {
              errMsg = backendData?.message || '본인인증 검증에 실패했습니다.';
            }
            if (typeof errMsg === 'string' && (errMsg.includes('Already verified') || errMsg.includes('이미 가입된 이름입니다') || errMsg.includes('이미 등록된') || errMsg.includes('이미 가입된'))) {
              const baseMsg = errMsg.trim().endsWith('.') ? errMsg.trim() : `${errMsg.trim()}.`;
              errMsg = `${baseMsg}\n다른 계정으로 이미 작가 등록 내역이 있는지 확인해 주세요. 만약 그렇지 않다면 북밋 관리자에게 메일을 보내주시기 바랍니다.`;
            }
            setMessage(errMsg);
            if (typeof setRealNameAuthError === 'function') {
              setRealNameAuthError(errMsg);
            }
            if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(false);
          }
        } catch (error: any) {
          setVerificationStatus('failure');
          const errData = error.response?.data;
          const errMsg = typeof errData === 'string' ? errData : errData?.message || error.message;

          let userErrMsg = '';
          if (typeof errMsg === 'string' && (errMsg.includes('Already verified') || errMsg.includes('이미 가입된 이름입니다') || errMsg.includes('이미 등록된') || errMsg.includes('이미 가입된'))) {
            const baseMsg = errMsg.trim().endsWith('.') ? errMsg.trim() : `${errMsg.trim()}.`;
            userErrMsg = `${baseMsg}\n다른 계정으로 이미 작가 등록 내역이 있는지 확인해 주세요. 만약 그렇지 않다면 북밋 관리자에게 메일을 보내주시기 바랍니다.`;
          } else {
            userErrMsg = `본인인증 처리 중 오류: ${errMsg}`;
          }
          setMessage(userErrMsg);
          if (typeof setRealNameAuthError === 'function') {
            setRealNameAuthError(userErrMsg);
          }

          if (typeof setIsRealNameVerified === 'function') setIsRealNameVerified(false);
        } finally {
          localStorage.removeItem('identityVerificationId');
          localStorage.removeItem('authorName');
        }
      };

      verify();
    }
  }, []);
  return (

    <div className="flex-grow ">

      <div className={"flex flex-row gap-2"}>


        <input
          ref={internalNameRef}
          className={`flex-1 min-w-0 h-[48px] px-4 border rounded-lg focus:outline-none text-base ${errors.name ? 'border-red-500' : 'border-gray-300'
            } ${verificationStatus === 'success' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          type="text"
          placeholder={errors.name || '이름을 입력하세요.'}
          value={authorName}
          onChange={handleAuthorNameChange}
          readOnly={isVerificationStatusLoading || verificationStatus === 'success'}
        />
        <button
          type="button"
          className={`flex-shrink-0 font-medium transition-colors w-[110px] h-12 font-namsan-EB text-sm rounded-full ${verificationStatus === 'pending'
            ? 'bg-gray-400 cursor-not-allowed'
            : verificationStatus === 'success'
              ? 'bg-main text-white cursor-not-allowed'
              : 'bg-main text-white hover:bg-green-400'
            }`}
          onClick={() => realNameAuthentication(authorName)}
          disabled={isVerificationStatusLoading || verificationStatus === 'pending' || verificationStatus === 'success'}
        >
          {isVerificationStatusLoading
            ? '확인 중...'
            : verificationStatus === 'pending'
            ? '인증 중...'
            : verificationStatus === 'success'
              ? '인증 완료'
              : '실명 인증'}
        </button>
      </div>


      {message && (
        <p className={`text-sm mt-2 ${verificationStatus === 'success' ? 'text-green-600' : 'hidden'
          }`}>
          {message}
        </p>
      )}
    </div>
  );
});

export default PortoneTest;
